import Link from "next/link";

import { Order, OrderUser } from "../../types/order";
import OrderStatusEditor from "./orderStatusEditor.comp";
import PaymentStatusEditor from "./paymentStatusEditor.comp";
import PayType from "./payType.comp";
import { Tag } from "../reuseables/list.comp";
import { useTranslation } from "../../core/translation.core";
import DeliveryStatusDisplay from "./deliveryStatusDisplay.comp";
import { ORDER_REQUIRED_COLUMNS, OrderCheckedItemsState, OrderListColumnKey } from "../../pages/orders";

export default function OrderRow({ order, checkedItems }: { order: Order; checkedItems: OrderCheckedItemsState }) {
  const { t, msg } = useTranslation();

  const stockHoldstatus = (() => {
    switch (true) {
      case !!order.inventoryHold && order.inventoryHold.length > 0 && order.paymentStatus === "paid":
        return "Completed";

      case !!order.inventoryHold && order.inventoryHold.length > 0:
        return "Holding";

      case (!!order.inventoryHold && order.inventoryHold.length === 0) || order.paymentStatus === "refunded":
        return "Released";

      default:
        return "None";
    }
  })();
  const columns: { key: OrderListColumnKey; size: string }[] = [
    { key: "orderNumber", size: "100px" },
    { key: "date", size: "200px" },
    { key: "orderStatus", size: "120px" },
    { key: "paymentStatus", size: "120px" },
    { key: "deliveryStatus", size: "120px" },
    { key: "stockHoldStatus", size: "100px" },
    { key: "customer", size: "300px" },
    { key: "products", size: "minmax(300px,1fr)" },
    { key: "amount", size: "100px" },
    { key: "paymentType", size: "100px" },
    { key: "posSystem", size: "80px" },
  ];
  const activeColumns = columns.filter((col) => ORDER_REQUIRED_COLUMNS.includes(col.key) || checkedItems[col.key]);
  const gridTemplateColumns = activeColumns.map((col) => col.size).join(" ");
  const activeColumnsCount = activeColumns.length;
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;
  const showColumn = (key: OrderListColumnKey) => ORDER_REQUIRED_COLUMNS.includes(key) || checkedItems[key];

  return (
    <div
      attr-name="orders"
      attr-type="order-row"
      className="w-full grid grid-cols-[1fr_1fr] lg:grid gap-y-3 gap-x-6 hover:bg-gray-100 dark:hover:bg-gray-700 border-b px-6 py-3 group/edit"
      style={{ gridTemplateColumns: isDesktop ? gridTemplateColumns : undefined }}
    >
      {showColumn("orderNumber") && (
        <div className="col-span-2 lg:col-span-1">
          <Tag>#</Tag>
          <div>
            {order.orderNumber}
            {order.legacyOrderId && <div className="text-sm text-gray-500 dark:text-gray-400">{order.legacyOrderId}</div>}
          </div>
        </div>
      )}
      {showColumn("date") && (
        <div className="col-span-2 lg:col-span-1">
          <Tag>{t(msg.order.date)}</Tag>
          {order.orderAt}
        </div>
      )}
      {showColumn("orderStatus") && (
        <div>
          <Tag>{t(msg.order.orderStatus)}</Tag>
          <div className="order-status">
            <OrderStatusEditor order={order} />
          </div>
        </div>
      )}
      {showColumn("paymentStatus") && (
        <div className="payment-status">
          <Tag>{t(msg.order.paymentStatus)}</Tag>
          <PaymentStatusEditor order={order} />
        </div>
      )}
      {showColumn("deliveryStatus") && (
        <div>
          <Tag>{t(msg.delivery.deliveryStatus)}</Tag>
          <DeliveryStatusDisplay orderId={order.id} />
        </div>
      )}
      {showColumn("stockHoldStatus") && (
        <div className="col-span-2 lg:col-span-1">
          <Tag>{t(msg.order.stockHoldStatus)}</Tag>
          {stockHoldstatus}
        </div>
      )}
      {showColumn("customer") && (
        <div className="col-span-2 lg:col-span-1" data-email={order.user?.email}>
          <Tag>{t(msg.order.customer)}</Tag>
          <UserData user={order.user} />
        </div>
      )}
      {showColumn("products") && (
        <div className="col-span-2 lg:col-span-1" data-col="products">
          <Tag>{t(msg.product.products)}</Tag>
          <div className="">
            {order.products?.map((p) => (
              <div key={p.id} data-sku={p.sku}>
                <Link href={`/product/edit/${p.id}`} target="_blank" className="underline">
                  {p.sku}
                </Link>
                &nbsp; x {p.quantity}
              </div>
            ))}
          </div>
        </div>
      )}

      {showColumn("amount") && (
        <div className="lg:text-right">
          <Tag>{t(msg.order.total)}</Tag>${order.total}
        </div>
      )}
      {showColumn("paymentType") && (
        <div>
          <PayType order={order} />
        </div>
      )}
      {showColumn("posSystem") && (
        <div>
          {order.retailShop && (
            <div className="inline-block px-2 py-1 text-sm font-medium text-center rounded-lg focus:ring-4 focus:outline-none text-white bg-yellow-700">
              {order.retailShop?.name}
            </div>
          )}
        </div>
      )}
      <div className="flex gap-8 mt-2 opacity-0 group-hover/edit:opacity-100 transition-all" style={{ gridColumn: `span ${activeColumnsCount}` }}>
        <Link className="order-edit leading-8 underline cursor-pointer" href={`/order/${order.id}`}>
          {t(msg.form.edit)}
        </Link>
      </div>
    </div>
  );
}

const UserData = ({ user }: { user: OrderUser }) => {
  return (
    <>
      <Link className="product-edit underline cursor-pointer" href={`customer/${user?.id}`}>
        <p>{user?.email}</p>
        <p>
          {user?.lastName}&nbsp;{user?.firstName}
        </p>
        <p>{user?.mobile}</p>
      </Link>
    </>
  );
};
